/**
 * System Bootstrap Component
 * Provides admin tools for system initialization and data management
 */

import { useState } from 'react';
import {
  createTestAccounts,
  clearTables,
  seedData,
  resetAll,
} from '../../services/systemApi';
import './SystemBootstrap.css';

interface Result {
  step: string;
  status: 'loading' | 'success' | 'error';
  message: string;
}

const SystemBootstrap = () => {
  const [results, setResults] = useState<Result[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const addResult = (step: string, status: Result['status'], message: string) => {
    setResults(prev => [...prev, { step, status, message }]);
  };

  const updateLastResult = (status: Result['status'], message: string) => {
    setResults(prev => {
      const newResults = [...prev];
      if (newResults.length > 0) {
        newResults[newResults.length - 1] = {
          ...newResults[newResults.length - 1],
          status,
          message,
        };
      }
      return newResults;
    });
  };

  const clearResults = () => setResults([]);

  const handleCreateTestAccounts = async () => {
    setIsLoading(true);
    clearResults();
    addResult('createTestAccounts', 'loading', '正在建立測試帳號...');

    try {
      const response = await createTestAccounts();
      const { created, accounts } = response.data;
      updateLastResult('success', `成功建立 ${created} 個測試帳號`);
      accounts.forEach(account => {
        addResult(
          'account',
          'success',
          `${account.email} (${account.role}, ${account.plan})`
        );
      });
    } catch (err) {
      updateLastResult('error', err instanceof Error ? err.message : '建立測試帳號失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearTables = async () => {
    setIsLoading(true);
    clearResults();
    addResult('clearTables', 'loading', '正在清空資料表...');

    try {
      const response = await clearTables();
      const { cleared, tables } = response.data;
      updateLastResult('success', `成功清空 ${cleared} 個資料表: ${tables.join(', ')}`);
    } catch (err) {
      updateLastResult('error', err instanceof Error ? err.message : '清空資料表失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeedData = async () => {
    setIsLoading(true);
    clearResults();
    addResult('seedData', 'loading', '正在匯入展示資料...');

    try {
      const response = await seedData();
      updateLastResult('success', response.data.message);
    } catch (err) {
      updateLastResult('error', err instanceof Error ? err.message : '匯入展示資料失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetAll = async () => {
    setIsLoading(true);
    clearResults();
    addResult('resetAll', 'loading', '正在執行一鍵全部重啟...');

    try {
      const response = await resetAll();
      const { steps } = response.data;

      // Clear the loading result and add all steps
      setResults([]);

      steps.forEach(step => {
        addResult(step.name, step.status, step.message);
      });

      // Add summary
      const successCount = steps.filter(s => s.status === 'success').length;
      const errorCount = steps.filter(s => s.status === 'error').length;

      if (errorCount === 0) {
        addResult('summary', 'success', `所有步驟成功完成 (${successCount}/${steps.length})`);
      } else {
        addResult('summary', 'error', `部分步驟失敗 (成功: ${successCount}, 失敗: ${errorCount})`);
      }
    } catch (err) {
      updateLastResult('error', err instanceof Error ? err.message : '一鍵全部重啟失敗');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="system-bootstrap">
      {/* Warning Message */}
      <div className="system-bootstrap__warning">
        <div className="warning-icon">⚠️</div>
        <div className="warning-content">
          <div className="warning-title">測試環境專用</div>
          <div className="warning-description">
            這些操作僅適用於測試環境。清空資料表將會永久刪除所有資料，請謹慎使用。
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="system-bootstrap__actions">
        <button
          className="system-bootstrap__button"
          onClick={handleCreateTestAccounts}
          disabled={isLoading}
        >
          <span className="button-icon">👤</span>
          <span className="button-text">建立/重建測試帳號</span>
        </button>

        <button
          className="system-bootstrap__button"
          onClick={handleClearTables}
          disabled={isLoading}
        >
          <span className="button-icon">🗑️</span>
          <span className="button-text">清空資料表</span>
        </button>

        <button
          className="system-bootstrap__button"
          onClick={handleSeedData}
          disabled={isLoading}
        >
          <span className="button-icon">📦</span>
          <span className="button-text">匯入展示資料</span>
        </button>

        <button
          className="system-bootstrap__button system-bootstrap__button--primary"
          onClick={handleResetAll}
          disabled={isLoading}
        >
          <span className="button-icon">🔄</span>
          <span className="button-text">一鍵全部重啟</span>
        </button>
      </div>

      {/* Results Display */}
      {results.length > 0 && (
        <div className="system-bootstrap__results">
          <div className="results-header">
            <h4 className="results-title">執行結果</h4>
            <button
              className="results-clear"
              onClick={clearResults}
              disabled={isLoading}
            >
              清除
            </button>
          </div>
          <div className="results-list">
            {results.map((result, index) => (
              <div key={index} className={`system-bootstrap__result system-bootstrap__result--${result.status}`}>
                <span className="result-icon">
                  {result.status === 'loading' && '⏳'}
                  {result.status === 'success' && '✓'}
                  {result.status === 'error' && '✗'}
                </span>
                <div className="result-content">
                  <span className="result-step">{result.step}</span>
                  <span className="result-message">{result.message}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="system-bootstrap__overlay">
          <div className="spinner"></div>
          <span className="overlay-text">執行中...</span>
        </div>
      )}
    </div>
  );
};

export default SystemBootstrap;
